import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { markNis2, markNis2Sent, removeNis2, ticketNis2Query, type Nis2Stage, type Nis2StageKey, type TicketNis2 } from '../api/nis2'
import { toast } from '../ui/notify'
import './TicketBands.css'
import './TicketNis2Art23.css'

const TITLE = 'NIS2 Art. 23'

/**
 * Banda registrului Art. 23 NIS2 pe fisa tichetului: omul marcheaza incidentul
 * semnificativ, banda arata cele trei termene si da textul de lipit in PNRISC.
 * Fara drept, inainte de migrare sau cu capatul cazut nu se deseneaza.
 */
export function TicketNis2Art23({ id }: { id: number }) {
  const query = ticketNis2Query(id)
  const { data } = useQuery({ ...query, retry: false })
  const queryClient = useQueryClient()
  const [marking, setMarking] = useState(false)
  const [awareAt, setAwareAt] = useState('')
  const [confirmRemove, setConfirmRemove] = useState(false)

  const saved = (page: TicketNis2) => {
    queryClient.setQueryData(query.queryKey, page)
    setMarking(false)
    setConfirmRemove(false)
  }
  const failed = (e: unknown) => toast(e instanceof Error ? e.message : 'Something went wrong.', TITLE, 'error')
  const mark = useMutation({ mutationFn: () => markNis2(id, awareAt), onSuccess: saved, onError: failed })
  const sent = useMutation({ mutationFn: (stage: Nis2StageKey) => markNis2Sent(id, stage), onSuccess: saved, onError: failed })
  const remove = useMutation({ mutationFn: () => removeNis2(id), onSuccess: saved, onError: failed })
  const busy = mark.isPending || sent.isPending || remove.isPending

  if (!data?.ready) return null
  const report = data.report

  if (report === null) {
    if (!data.canEdit) return null
    return (
      <div className="tk-family tk-nis2 tk-nis2-unmarked">
        <div className="tk-family-row">
          <span className="tk-family-label">NIS2</span>
          {marking ? (
            <span className="tk-nis2-mark">
              <label htmlFor={`nis2-aware-${id}`}>When did you learn of it?</label>
              <input
                id={`nis2-aware-${id}`}
                type="datetime-local"
                className="form-control input-sm"
                value={awareAt}
                max={localNow()}
                onChange={(e) => setAwareAt(e.target.value)}
              />
              <button type="button" className="btn btn-danger btn-xs" disabled={busy} onClick={() => mark.mutate()}>
                Mark
              </button>
              <button type="button" className="btn btn-default btn-xs" disabled={busy} onClick={() => setMarking(false)}>
                Cancel
              </button>
            </span>
          ) : (
            <button
              type="button"
              className="btn btn-link btn-xs tk-nis2-open"
              onClick={() => {
                setAwareAt(localNow())
                setMarking(true)
              }}
            >
              Significant incident (NIS2 Art. 23)
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="tk-family tk-nis2">
      <div className="tk-family-row">
        <span className="tk-family-label">NIS2 Art. 23</span>
        <span>
          <strong>Significant incident</strong> <span className="text-muted">· learned of it {report.aware_at} · report in PNRISC (DNSC)</span>
          {data.canEdit && report.can_remove && (
            <span className="tk-nis2-remove">
              {confirmRemove ? (
                <>
                  {' '}
                  <button type="button" className="btn btn-danger btn-xs" disabled={busy} onClick={() => remove.mutate()}>
                    Yes, remove
                  </button>{' '}
                  <button type="button" className="btn btn-default btn-xs" onClick={() => setConfirmRemove(false)}>
                    Keep
                  </button>
                </>
              ) : (
                <button type="button" className="btn btn-link btn-xs" onClick={() => setConfirmRemove(true)}>
                  Not significant
                </button>
              )}
            </span>
          )}
        </span>
      </div>
      {report.stages.map((stage) => (
        <StageRow
          key={stage.key}
          stage={stage}
          draft={report.drafts[stage.key]}
          canEdit={data.canEdit}
          busy={busy}
          onSent={() => sent.mutate(stage.key)}
        />
      ))}
    </div>
  )
}

function StageRow({ stage, draft, canEdit, busy, onSent }: { stage: Nis2Stage; draft: string; canEdit: boolean; busy: boolean; onSent: () => void }) {
  const copy = () => {
    void navigator.clipboard
      .writeText(draft)
      .then(() => toast(`${stage.label}: text copied, paste it in PNRISC.`, TITLE))
      .catch(() => toast('The browser did not allow copying.', TITLE, 'error'))
  }

  return (
    <div className={`tk-family-row tk-nis2-stage tk-nis2-${stage.state}`} data-stage={stage.key}>
      <span className="tk-family-label">{stage.label}</span>
      <span className="tk-nis2-line">
        due {stage.due}
        {stage.estimated && <span className="text-muted"> (estimated, counts from the notification)</span>} <StageState stage={stage} />
        {canEdit && stage.can_send && (
          <>
            {' '}
            <button type="button" className="btn btn-default btn-xs" disabled={busy} onClick={onSent}>
              Mark as sent
            </button>
          </>
        )}{' '}
        <button type="button" className="btn btn-link btn-xs" onClick={copy}>
          Copy text
        </button>
      </span>
    </div>
  )
}

function StageState({ stage }: { stage: Nis2Stage }) {
  if (stage.state === 'sent') return <span className="label label-success">sent {stage.sent_at}</span>
  if (stage.state === 'sent_late') return <span className="label label-warning">sent late {stage.sent_at}</span>
  if (stage.state === 'overdue') return <span className="label label-danger">overdue</span>
  return <span className="text-muted">{left(stage.minutes_left ?? 0)}</span>
}

/** `1d 1h left`, `5h 20m left`, `45m left`. */
export function left(minutes: number): string {
  const days = Math.floor(minutes / 1440)
  const hours = Math.floor((minutes % 1440) / 60)
  const mins = minutes % 60
  if (days > 0) return `${days}d ${hours}h left`
  if (hours > 0) return `${hours}h ${mins}m left`
  return `${mins}m left`
}

/** Acum, in forma lui `datetime-local` (ora browserului). */
function localNow(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}
